// src/components/auth/ProtectedRoute.js
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import LoadingPage from '../common/LoadingPage';
import RegistrationOverlay from '../public/RegistrationOverlay';

/**
 * Componente para proteger rutas que requieren autenticación
 * @param {Object} props
 * @param {React.ReactNode} props.children - Componentes a renderizar si está autenticado
 * @param {Array} props.allowedRoles - Roles permitidos para acceder a la ruta
 * @param {Array} props.allowedMemberships - Membresías permitidas para acceder a la ruta
 * @param {String} props.redirectTo - Ruta a la que redirigir si no está autenticado
 * @param {Number} props.timeout - Tiempo máximo en ms para verificar autenticación (default: 3000)
 * @param {Boolean} props.previewForAnonymous - Si true, los anonimos ven el dashboard real
 *   con un overlay de registro encima (estilo Instagram) en vez de redirigir al login.
 *   Los logueados con rol no permitido siguen siendo redirigidos como antes.
 */
const ProtectedRoute = ({
  children,
  allowedRoles = [],
  allowedMemberships = [],
  redirectTo = '/login',
  timeout = 3000,
  previewForAnonymous = false
}) => {
  const { isAuthenticated, user, loading } = useAuth();
  const [localLoading, setLocalLoading] = useState(true);
  const [authTimedOut, setAuthTimedOut] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Si la autenticación ya se cargó, actualizamos el estado local
    if (!loading) {
      setLocalLoading(false);
    }

    // Establecer un timeout para evitar pantallas de carga infinitas
    const timeoutId = setTimeout(() => {
      if (localLoading) {
        console.warn('Timeout de autenticación en ruta protegida:', location.pathname);
        setAuthTimedOut(true);
        setLocalLoading(false);
      }
    }, timeout);

    return () => clearTimeout(timeoutId);
  }, [loading, localLoading, location.pathname, timeout]);

  // Mostrar pantalla de carga mientras se verifica la autenticación
  if (localLoading && !authTimedOut) {
    return <LoadingPage message="Verificando acceso..." />;
  }

  // Si no está autenticado, dos comportamientos:
  // - previewForAnonymous=true: renderiza los hijos (el dashboard real) y monta
  //   un overlay de registro encima estilo Instagram. Cebo SEO/conversion.
  // - default: redirige al login guardando la ruta actual.
  if (!isAuthenticated || authTimedOut) {
    if (previewForAnonymous) {
      return (
        <>
          {children}
          <RegistrationOverlay />
        </>
      );
    }
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Verificar si el usuario tiene un rol permitido (si se especificaron roles)
  const userType = user?.user_type || 'individual';
  const isAdmin = user?.is_admin || false;
  const membershipLevel = user?.membership_level || 'bronze';

  // Los administradores pueden acceder a todas las rutas
  if (isAdmin) {
    return children;
  }

  // Comprobar si el tipo de usuario está permitido
  const hasAllowedRole = allowedRoles.length === 0 || allowedRoles.includes(userType);
  
  // Comprobar si la membresía del usuario está permitida
  const hasAllowedMembership = allowedMemberships.length === 0 || 
    allowedMemberships.includes(membershipLevel);

  // Si el usuario no tiene el rol o membresía permitidos, redirigir
  if (!hasAllowedRole || !hasAllowedMembership) {
    // Determinar la redirección adecuada según el tipo de usuario
    let redirectPath = '/';
    
    if (userType === 'company') {
      redirectPath = '/company';
    } else if (userType === 'individual') {
      if (membershipLevel === 'gold') {
        redirectPath = '/gold-member';
      } else if (membershipLevel === 'silver') {
        redirectPath = '/silver-member';
      } else {
        redirectPath = '/bronze-member';
      }
    }
    
    return <Navigate to={redirectPath} replace />;
  }

  // Si el usuario está autenticado y tiene los permisos necesarios, mostrar los componentes hijos
  return children;
};

export default ProtectedRoute;